/** @jsxImportSource @emotion/react */

import {
  Card,
  Errors,
  Input,
  ListItem,
  Select,
  Setters,
  Switch,
  Typography,
  createStyles,
  useTheme,
} from '@quickbit/qb-design-system';
import { uniqueId } from 'lodash';
import { RelatedPersonFormData } from 'tabs/Merchants/types';
import { ContactInformationForm } from './ContactInformationForm';
import { DateInput } from './DateInput';

interface Props {
  data: RelatedPersonFormData[];
  editable: boolean;
  setters: Setters<RelatedPersonFormData[]>;
  errors: Errors<RelatedPersonFormData[]>;
}

export const RelatedPeople = ({ data, errors, editable, setters }: Props) => {
  const { spacing } = useTheme();
  const styles = useStyles();

  return (
    <>
      <div
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: spacing.xxxs,
        }}
      >
        <Typography variant="bodyBold">Related people</Typography>
      </div>
      {data.map(
        (
          {
            firstName,
            lastName,
            isPep,
            dateOfBirth,
            userReference,
            type,
            role,
            placeOfBirth,
            ssn,
            address,
          },
          idx
        ) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const id = userReference!;
          return (
            <Card key={id}>
              <div
                css={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: spacing.xxxs,
                }}
              >
                <Select
                  label="Type"
                  options={[
                    { label: 'Board member', value: 'BOARD_MEMBER' },
                    {
                      label: 'Senior management',
                      value: 'SENIOR_MANAGEMENT',
                    },
                    { label: 'Shareholder', value: 'SHAREHOLDER' },
                  ]}
                  values={[type]}
                  onSelect={([type]) => setters.list[idx].type.set(type)}
                  helperText={errors[idx].type ?? undefined}
                  error={!!errors[idx].type}
                />
                <Input
                  label="First name"
                  value={firstName}
                  disabled={!editable}
                  onChange={(e) =>
                    setters.list[idx].firstName.set(e.target.value)
                  }
                  error={!!errors[idx].firstName}
                  helperText={errors[idx].firstName}
                />
                <Input
                  label="Last name"
                  value={lastName}
                  disabled={!editable}
                  onChange={(e) =>
                    setters.list[idx].lastName.set(e.target.value)
                  }
                  error={!!errors[idx].lastName}
                  helperText={errors[idx].lastName}
                />
                <ListItem
                  css={[styles.listItem, !editable && styles.listItemDisabled]}
                  title="Is PEP"
                  action={
                    <Switch
                      checked={isPep}
                      onChange={(checked) =>
                        setters.list[idx].isPep.set(checked)
                      }
                      disabled={!editable}
                    />
                  }
                  disabled={!editable}
                />
                <DateInput
                  title="Date of birth"
                  value={dateOfBirth}
                  disabled={!editable}
                  onChange={(d) => setters.list[idx].dateOfBirth.set(d)}
                  error={!!errors[idx].dateOfBirth}
                  helperText={errors[idx].dateOfBirth ?? ''}
                />
                <Input
                  label="Social security number"
                  value={ssn}
                  disabled={!editable}
                  onChange={(e) => setters.list[idx].ssn.set(e.target.value)}
                  error={!!errors[idx].ssn}
                  helperText={errors[idx].ssn}
                />

                <div css={{ gridColumn: '1 / -1' }}>
                  <ContactInformationForm
                    label="Address"
                    data={address}
                    editable={editable}
                    setters={setters.list[idx].address}
                    errors={errors[idx].address}
                  />
                </div>
                {type && type !== 'SHAREHOLDER' && placeOfBirth && (
                  <Input
                    label="Place of birth"
                    value={placeOfBirth}
                    disabled={!editable}
                    onChange={(e) =>
                      setters.list[idx].placeOfBirth.set(e.target.value)
                    }
                    error={!!errors[idx].placeOfBirth}
                    helperText={errors[idx].placeOfBirth ?? ''}
                  />
                )}
                {type && type !== 'SHAREHOLDER' && (
                  <Input
                    label="Role"
                    value={role ?? ''}
                    disabled={!editable}
                    onChange={(e) => setters.list[idx].role.set(e.target.value)}
                  />
                )}
                <ListItem
                  title="Remove person"
                  css={[styles.listItem, !editable && styles.listItemDisabled]}
                  icon="trash"
                  onClick={() =>
                    setters.set(
                      data.filter((p) => p.userReference !== userReference)
                    )
                  }
                  disabled={!editable}
                />
              </div>
            </Card>
          );
        }
      )}
      <ListItem
        css={[styles.listItem, !editable && styles.listItemDisabled]}
        title="Add related person"
        icon={'plus'}
        onClick={() =>
          setters.set([
            ...data,
            {
              dateOfBirth: null,
              firstName: '',
              isPep: false,
              lastName: '',
              userReference: uniqueId('new-'),
              role: null,
              placeOfBirth: null,
              type: '',
              ssn: '',
              address: {
                postalRegion: '',
                postalNumber: '',
                country: '',
                address1: '',
              },
            },
          ])
        }
        disabled={!editable}
      />
    </>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  listItem: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
  listItemDisabled: {
    ':hover': {
      borderColor: 'inherit',
    },
  },
}));
